import React, { useState, useEffect, useCallback } from 'react';
import { 
  Download, 
  FileText, 
  BarChart3, 
  Loader, 
  Search, 
  User, 
  Users, 
  Filter,
  Share2,
  CheckCircle,
  AlertCircle,
  XCircle,
  ChevronDown,
  X,
  ChevronRight,
  FileDown,
  Send,
  BookOpen
} from 'lucide-react';
import toast from 'react-hot-toast';
import resultApi from '../../api/services/resultApi';
import EXAM_API from '../../api/services/examApi';
import { useData } from '../../contexts/DataContext';
import sectionApi from '../../api/services/sectionApi';

const Reports = () => {
  const { classes: apiClasses, loadStudents, students: contextStudents, currentAcademicYear } = useData();
  const [exams, setExams] = useState([]);
  const [students, setStudents] = useState([]);
  
  // Selection State
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(''); // Registration ID or empty for all
  const [selectedExam, setSelectedExam] = useState('');
  
  // Section States
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState('');
  const [loadingSections, setLoadingSections] = useState(false);
  
  // Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  
  // Loading States
  const [generatingReport, setGeneratingReport] = useState(false);
  const [generatingPerformance, setGeneratingPerformance] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);

  // Load students when class is selected
  const loadClassStudents = useCallback(async () => {
    if (!selectedClass) return;
    setLoadingStudents(true);
    try {
      await loadStudents(selectedClass, selectedSection || null);
    } catch (error) {
      toast.error('Failed to load students');
      setStudents([]);
    } finally {
        setLoadingStudents(false);
    }
  }, [selectedClass, selectedSection, loadStudents]);

  // Load students when selections change
  useEffect(() => {
    if (selectedClass) {
      loadClassStudents();
    } else {
      setStudents([]);
      setSearchQuery('');
      setSelectedStudent('');
    }
  }, [selectedClass, selectedSection, loadClassStudents]);

  // Update local students when context students change
  useEffect(() => {
    if (contextStudents) {
      setStudents(contextStudents);
    }
  }, [contextStudents]);

  // Fetch sections when class/unit changes
  useEffect(() => {
    if (!selectedClass) {
        setSections([]);
        setSelectedSection('');
        return;
    }
    
    const fetchSections = async () => {
        setLoadingSections(true);
        try {
            const response = await sectionApi.getSectionsByUnit(selectedClass);
            if (response.success) {
                setSections(response.data || []);
            }
        } catch (error) {
        } finally {
            setLoadingSections(false);
        }
    };
    fetchSections();
  }, [selectedClass]);

  const loadExams = async () => {
    // Only load exams if a class is selected
    if (!selectedClass) {
      setExams([]);
      return;
    }

    try {
      const academicYear = currentAcademicYear || `${new Date().getFullYear()}-${(new Date().getFullYear() + 1).toString().slice(-2)}`;
      
      // Fetch only exams assigned to the selected class
      const response = await resultApi.getAssignedExamsForClass(selectedClass, academicYear);
      
      if (response.success) {
        const examsList = response.data || [];
        const normalized = examsList.map(exam => ({
          ...exam,
          id: exam._id || exam.id || exam.examId
        }));
        setExams(normalized);
      } else {
        setExams([]);
      }
    } catch (error) {
      setExams([]);
    }
  };

  // Load exams when class or academic year changes
  useEffect(() => {
    loadExams();
  }, [selectedClass, currentAcademicYear]);

  // Search students
  useEffect(() => {
    if (!searchQuery.trim() || selectedStudent) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = students.filter(s => 
      s.firstName?.toLowerCase().includes(query) ||
      s.lastName?.toLowerCase().includes(query) ||
      s.studentRegistrationId?.toLowerCase().includes(query) ||
      s.admissionNo?.toLowerCase().includes(query)
    ).slice(0, 10);

    setSearchResults(filtered);
    setShowSearchResults(true);
  }, [searchQuery, students, selectedStudent]);

  const handleStudentSelect = (student) => {
    setSelectedStudent(student.studentRegistrationId || student._id);
    setSearchQuery(`${student.firstName} ${student.lastName} (${student.studentRegistrationId || student.admissionNo || 'N/A'})`);
    setShowSearchResults(false);
  };

  const handleClearStudent = () => {
    setSelectedStudent('');
    setSearchQuery('');
    setShowSearchResults(false);
  };

  // Handlers
  const handleDownloadReportCard = async () => {
    if (!selectedExam || !selectedClass) {
      toast.error('Please select an exam and class');
      return;
    }

    setGeneratingReport(true);
    try {
      const studentIds = selectedStudent ? [selectedStudent] : null;
      await resultApi.downloadReportCards(selectedExam, selectedClass, selectedSection || null, studentIds);
      toast.success(selectedStudent ? '✅ Report card downloaded!' : '✅ Report cards downloaded!');
    } catch (error) {
      toast.error('Failed to generate report card(s)');
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleDownloadPerformance = async () => {
    if (!selectedExam || !selectedClass) {
      toast.error('Please select an exam and class');
      return;
    }

    setGeneratingPerformance(true);
    try {
      await resultApi.downloadClassPerformance(selectedExam, selectedClass, selectedSection || null);
      toast.success('✅ Class performance report downloaded!');
    } catch (error) {
      toast.error('Failed to generate performance report');
    } finally {
      setGeneratingPerformance(false);
    }
  };

  const handleSendReport = async () => {
    if (!selectedExam || !selectedClass) {
        toast.error('Please select exam and class');
        return;
    }

    setSendingReport(true);
    try {
      // Simulate sending report - following user request to include section
      const payload = {
          examId: selectedExam,
          unitId: selectedClass,
          sectionId: selectedSection || null, // Critical: send section d if we have unit section
          studentId: selectedStudent || null
      };
      await new Promise(resolve => setTimeout(resolve, 1500)); 
      
      toast.success('✅ Reports sent successfully');
    } catch (error) {
      toast.error('Failed to send report');
    } finally {
      setSendingReport(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
            <div>
                <h2 className="text-lg font-bold text-slate-900">Report Generation</h2>
                <p className="text-xs text-slate-500">Generate and distribute official report cards and performance analytics</p>
            </div>
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)', borderColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 20%, white)' }}>
                <BarChart3 className="h-4 w-4" style={{ color: 'var(--primary-color, #4f46e5)' }} />
                <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: 'color-mix(in srgb, var(--primary-color, #4f46e5) 80%, black)' }}>Analytics Suite v2.0</span>
            </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Step 1: Select Class */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
                <span className="flex items-center justify-center w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-bold">1</span>
                Select Class
            </label>
            <div className="relative">
              <select
                value={selectedClass}
                onChange={(e) => {
                  setSelectedClass(e.target.value);
                  setSelectedSection('');
                  setSelectedExam('');
                  setSelectedStudent('');
                }}
                className="w-full bg-white border border-gray-300 rounded-md py-2.5 pl-4 pr-10 text-sm focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none appearance-none cursor-pointer"
              >
                <option value="">Choose Class...</option>
                {apiClasses.map(cls => (
                  <option key={cls._id} value={cls._id}>{cls.unitName}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Step 2: Select Section */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
                <span className={`flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${selectedClass ? 'bg-slate-900 text-white' : 'bg-gray-200 text-gray-400'}`}>2</span>
                Select Section
            </label>
            <div className="relative">
              <select
                value={selectedSection}
                disabled={!selectedClass}
                onChange={(e) => setSelectedSection(e.target.value)}
                className={`w-full border rounded-md py-2.5 pl-4 pr-10 text-sm focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none appearance-none ${selectedClass ? 'bg-white border-gray-300 cursor-pointer' : 'bg-gray-50 border-gray-200 cursor-not-allowed'}`}
              >
                <option value="">{sections.length > 0 ? 'All Sections' : 'No Sections Available'}</option>
                {sections.map(section => (
                  <option key={section._id} value={section._id}>
                    Section {section.name || section.sectionName}
                  </option>
                ))}
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400">
                  {loadingSections ? <Loader size={14} className="animate-spin" /> : <ChevronDown size={16} />}
              </div>
            </div>
          </div>

          {/* Step 3: Select Exam */}
          <div className="space-y-2">
            <label className="text-xs font-medium text-gray-500 uppercase flex items-center gap-2">
                <span className={`flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold ${selectedClass ? 'bg-slate-900 text-white' : 'bg-gray-200 text-gray-400'}`}>3</span>
                Select Exam
            </label>
            <div className="relative">
              <select
                value={selectedExam}
                disabled={!selectedClass}
                onChange={(e) => setSelectedExam(e.target.value)}
                className={`w-full border rounded-md py-2.5 pl-4 pr-10 text-sm focus:border-slate-900 focus:ring-1 focus:ring-slate-900 outline-none appearance-none ${selectedClass ? 'bg-white border-gray-300 cursor-pointer' : 'bg-gray-50 border-gray-200 cursor-not-allowed'}`}
              >
                <option value="">{exams.length > 0 ? 'Choose Exam Series...' : 'No Exams Assigned'}</option>
                {exams.map(exam => (
                  <option key={exam.id} value={exam.id}>
                    {exam.examName} ({exam.examType})
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Student Search */}
        <div className="mt-6 pt-6 border-t border-gray-100">
          <div className="max-w-xl">
            <label className="text-xs font-bold text-slate-700 uppercase tracking-wide mb-2 block">Individual Student (Optional)</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery && !selectedStudent && setShowSearchResults(true)}
                disabled={!selectedClass}
                placeholder={!selectedClass ? "Select a class first" : "Search student by name or ID..."}
                className="w-full bg-gray-50 border border-gray-200 rounded-lg py-2.5 pl-10 pr-10 text-sm font-medium input-field transition-all outline-none"
              />
              {selectedStudent && (
                <button
                  onClick={handleClearStudent}
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-6 w-6 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <X size={16} />
                </button>
              )}

              {showSearchResults && searchResults.length > 0 && (
                <div className="absolute z-40 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2">
                  {searchResults.map((student) => (
                    <button
                      key={student._id}
                      onClick={() => handleStudentSelect(student)}
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0"
                    >
                      <div className="h-8 w-8 rounded-lg bg-gray-100 flex items-center justify-center text-slate-400 font-bold text-xs uppercase">
                        {student.firstName[0]}{student.lastName[0]}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-slate-900">{student.firstName} {student.lastName}</p>
                        <p className="text-[10px] text-slate-500 font-medium">ID: {student.studentRegistrationId || student.admissionNo}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 transition-all duration-500 ${selectedExam ? 'opacity-100 translate-y-0' : 'opacity-40 pointer-events-none translate-y-4'}`}>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 flex flex-col items-center text-center">
            <div className="h-16 w-16 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)', color: 'var(--primary-color, #4f46e5)' }}>
                <FileDown size={32} />
            </div>
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-tight mb-2">Download Report Cards</h3>
            <p className="text-xs text-slate-500 mb-6 px-4">Generate official printable report cards in PDF format for the selection.</p>
            <button
                onClick={handleDownloadReportCard}
                disabled={generatingReport || !selectedClass || !selectedExam}
                className="w-full py-2.5 rounded-lg text-white text-xs font-bold transition-all shadow-sm disabled:opacity-50 flex items-center justify-center gap-2 btn-primary"
            >
                {generatingReport ? <Loader className="animate-spin" size={14} /> : <Download size={14} />}
                Generate PDF
            </button>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 flex flex-col items-center text-center">
            <div className="h-16 w-16 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: 'color-mix(in srgb, var(--primary-color, #4f46e5) 10%, white)', color: 'var(--primary-color, #4f46e5)' }}>
                <BarChart3 size={32} />
            </div>
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-tight mb-2">Class Performance</h3>
            <p className="text-xs text-slate-500 mb-6 px-4">Download a detailed analysis and summary of performance for this cohort.</p>
            <button
                onClick={handleDownloadPerformance}
                disabled={generatingPerformance || !selectedClass || !selectedExam}
                className="w-full py-2.5 rounded-lg bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 transition-all shadow-sm disabled:opacity-50 flex items-center justify-center gap-2"
            >
                {generatingPerformance ? <Loader className="animate-spin" size={14} /> : <FileText size={14} />}
                Analysis Report
            </button>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 flex flex-col items-center text-center">
            <div className="h-16 w-16 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: 'color-mix(in srgb, var(--secondary-color, #7c3aed) 10%, white)', color: 'var(--secondary-color, #7c3aed)' }}>
                <Send size={32} />
            </div>
            <h3 className="text-sm font-bold text-slate-900 uppercase tracking-tight mb-2">Send Reports</h3>
            <p className="text-xs text-slate-500 mb-6 px-4">Dispatch digital reports directly to parents via WhatsApp or Portal.</p>
            <button
                onClick={handleSendReport}
                disabled={sendingReport || !selectedClass || !selectedExam}
                className="w-full py-2.5 rounded-lg text-white text-xs font-bold transition-all shadow-sm disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: 'var(--secondary-color, #7c3aed)' }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--secondary-color, #7c3aed) 80%, black)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'var(--secondary-color, #7c3aed)'}
            >
                {sendingReport ? <Loader className="animate-spin" size={14} /> : <Send size={14} />}
                Dispatch Reports
            </button>
        </div>
      </div>
    </div>
  );
};

export default Reports;
